/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { Placeholder, useFlags } from 'sarsaparilla';
import { useWizardContext } from '../context/WizardContext';
import { usePassDetailsQuery } from '../../../api/queries';
import type { PassDetails, PassType } from '../../../api/interagency-pass-types';
import InteragencyPassCard from '../../shared/InteragencyPassCard';
import './WizardResults.scss';

/**
 * Wizard results display order: Access, Senior (lifetime before annual), Every Kid
 * Outdoors, Military (lifetime before annual). Other types keep recommendation order
 * after these.
 */
const PASS_RESULTS_ORDER: PassType[] = [
    'access',
    'senior-lifetime',
    'senior-annual',
    'fourth-grade',
    'military-lifetime',
    'military-annual',
];

function passResultSortIndex(type: PassType): number {
    const idx = PASS_RESULTS_ORDER.indexOf(type);
    return idx === -1 ? PASS_RESULTS_ORDER.length : idx;
}

function WizardResults() {
    const { recommendations, closeWizard } = useWizardContext();
    const { overrideAtbPassYear } = useFlags();
    const { data: passDetails, isLoading } = usePassDetailsQuery();

    if (isLoading) {
        return (
            <div className="wizard-results">
                <Placeholder className="mb-3" height="200px" />
                <Placeholder className="mb-3" height="200px" />
            </div>
        );
    }

    // Filter pass details to only recommended types
    const sitePassRecs = recommendations.filter((r) => r.isSitePass);
    const iapRecs = recommendations.filter((r) => !r.isSitePass);
    const matchedPasses = iapRecs
        .map((rec) => passDetails?.find((p) => p.type === rec.passType))
        .filter((p): p is PassDetails => Boolean(p))
        .sort((a, b) => passResultSortIndex(a.type) - passResultSortIndex(b.type));

    const totalResults = matchedPasses.length + sitePassRecs.length;

    return (
        <div className="wizard-results">
            {matchedPasses.map((pass) => (
                <InteragencyPassCard
                    key={pass.type}
                    pass={pass}
                    overrideAtbPassYear={overrideAtbPassYear}
                    gaTrackingId="490917311444"
                    gaTrackingValue={pass.type}
                />
            ))}
            {sitePassRecs.map((_rec, i) => (
                <InteragencyPassCard
                    key={`site-pass-${i}`}
                    pass={null as never}
                    isSitePass
                    onLearnMoreClick={closeWizard}
                    gaTrackingId="490917311444"
                    gaTrackingValue="Site Pass"
                />
            ))}
            {totalResults === 0 && (
                <p>
                    We couldn&apos;t find a matching pass. Please try again or browse all
                    available passes on the <a href="/pass">Passes page</a>.
                </p>
            )}
        </div>
    );
}

export { WizardResults };
